import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Mutation, Query } from '@apollo/client/react/components';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import { Grid } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { toastError, toastSuccess } from 'bench-internal-theme';
import { Spinner } from 'spinner';
import LedgerForm from './LedgerForm';
import {
  CREATE_LEDGER_ACCOUNT,
  MAP_CONNECTION_TO_ACCOUNT,
  UNMAP_FDS_ACCOUNT_LEDGER,
  UPDATE_LEDGER_ACCOUNT,
} from './mutations';
import { GET_LEDGER_ACCOUNT } from './queries';
import { findFdsAccountByLedgerId, findFdsAccountByAccountId, thirdPartyLoginStatus } from './util';
import { EditLedgerContainer, materialStyles } from './EditLedgerContainer.styles';
import LedgerOptionsDataProvider, { LedgerOptionsDataConsumer } from './LedgerOptionsDataProvider';

const hideModalToRefetchData = () => {
  const modal = window.angular
    ?.element(document.querySelector('[ng-app]'))
    ?.injector()
    ?.get('$mdDialog');
  if (modal) modal.hide();
};

const handleClose = () => {
  const modal = window.angular
    ?.element(document.querySelector('[ng-app]'))
    .injector()
    .get('$mdDialog');
  return modal ? modal.cancel() : null;
};

// TODO refactor this component into a separate container component so that all the mutations
// can be passed as props. This made sense as first, but now there are many mutations in this function.
const EditLedger = props => {
  const [isMappingConnection, setIsMappingConnection] = React.useState(false);
  const [shouldFinishAndClose, setShouldFinishAndClose] = React.useState(false);
  const [isMutationComplete, setIsMutationComplete] = React.useState(false);

  // eslint-disable-next-line consistent-return
  const getRoleName = categoryId => {
    // eslint-disable-next-line default-case
    switch (categoryId) {
      case '1':
        return 'BANK_ACCOUNT';
      case '3':
        return 'CREDIT_CARD';
      case '5':
        return 'EQUITY_ACCOUNT';
      case '6':
        return 'STATEMENT_PROCESSING_ACCOUNT';
      case '7':
        return 'STATEMENT_PROCESSING_ACCOUNT';
      case '8':
        return 'STATEMENT_PROCESSING_ACCOUNT';
    }
  };

  const selectInputData = ({
    name,
    details,
    isTemporary,
    currencyId,
    categoryId,
    financialInstitutionProductId,
    statementDayOfMonth,
    dateActive,
    dateInactive,
    collectionMethod,
    optimizationStatus,
    thirdPartyLoginAvailable,
    volume,
    autoPostEnabled,
    role,
    accountType,
    isFunctional,
    isAutoGenerated,
    accountLastFourDigits,
  }) => ({
    name,
    details,
    isTemporary,
    currencyId,
    categoryId,
    financialInstitutionProductId: isFunctional ? financialInstitutionProductId : null,
    // The API returns statementDayOfMonth as a number, but GQL requires it to be a string
    statementDayOfMonth: isFunctional ? statementDayOfMonth.toString() : null,
    dateActive: isFunctional ? dateActive : null,
    dateInactive: isFunctional ? dateInactive : null,
    collectionMethod: isFunctional ? collectionMethod : null,
    optimizationStatus: isFunctional ? optimizationStatus : null,
    thirdPartyLoginAvailable: thirdPartyLoginAvailable === thirdPartyLoginStatus.AVAILABLE,
    volume,
    autoPostEnabled,
    role: isFunctional && !role ? getRoleName(categoryId) : role,
    accountType,
    isAutoGenerated,
    accountLastFourDigits:
      isFunctional && accountLastFourDigits && accountLastFourDigits.length
        ? accountLastFourDigits.toString()
        : null,
  });

  const mapFdsConnection = (newFdsAccount, mapConnectionToAccount, ledgerId) =>
    mapConnectionToAccount({
      variables: {
        clientId: props.clientId,
        connection: {
          source: newFdsAccount?.source,
          id: newFdsAccount?.id,
        },
        accountId: ledgerId,
      },
      refetchQueries: ['FdsConnectionCollection'],
    });

  const unmapFdsConnection = (previousFdsAccountId, unmapFDSAccountLedger) =>
    unmapFDSAccountLedger({
      variables: {
        accountId: previousFdsAccountId,
        ledgerId: null,
        dryRun: false,
      },
      refetchQueries: ['FdsConnectionCollection'],
    });

  const handleFdsConnectionMapping = (
    newFdsAccount,
    previousFdsAccount,
    unmapFDSAccountLedger,
    mapConnectionToAccount,
    ledgerId,
  ) => {
    if (!newFdsAccount && previousFdsAccount) {
      return unmapFdsConnection(previousFdsAccount?.id, unmapFDSAccountLedger);
    }

    if (newFdsAccount?.id !== previousFdsAccount?.ids) {
      return mapFdsConnection(newFdsAccount, mapConnectionToAccount, ledgerId);
    }
  };

  const handleConnectionMapping = (
    formData,
    ledgerId,
    mapConnectionToAccount,
    referenceData,
    unmapFDSAccountLedger,
  ) => {
    const newFdsAccount = findFdsAccountByAccountId(
      referenceData.client.financialDataConnections,
      formData?.integration,
    );

    const previousFdsAccount = findFdsAccountByLedgerId(
      referenceData.client.financialDataConnections,
      ledgerId,
    );

    if (newFdsAccount || previousFdsAccount) {
      return handleFdsConnectionMapping(
        newFdsAccount,
        previousFdsAccount,
        unmapFDSAccountLedger,
        mapConnectionToAccount,
        ledgerId,
      );
    }
  };

  const handleFormSubmit = (
    data,
    createLedgerAccount,
    updateLedgerAccount,
    mapConnectionToAccount,
    referenceData,
    unmapFDSAccountLedger,
    isSaveAndFinish,
  ) => {
    const { ledgerId, clientId } = props;
    if (data.integration) {
      setIsMappingConnection(true);
    }
    if (isSaveAndFinish) {
      setShouldFinishAndClose(isSaveAndFinish);
    }

    if (ledgerId) {
      updateLedgerAccount({
        variables: {
          ledgerAccountId: ledgerId.toString(),
          ledgerAccountInput: selectInputData(data),
        },
      }).then(updatedLedger =>
        handleConnectionMapping(
          data,
          updatedLedger?.data?.updateLedgerAccount?.id,
          mapConnectionToAccount,
          referenceData,
          unmapFDSAccountLedger,
        ),
      );
    } else {
      createLedgerAccount({
        variables: { clientId, ledgerAccount: selectInputData(data) },
      }).then(createdLedger => {
        handleConnectionMapping(
          data,
          createdLedger?.data?.createLedgerAccount?.id,
          mapConnectionToAccount,
          referenceData,
          unmapFDSAccountLedger,
        );
      });
    }
  };

  const handleConnectionMappingComplete = () => {
    toastSuccess('Successfully mapped ledger to connected account');
  };

  const handleError = error => {
    toastError('Ledger Not Saved', `Your changes were not saved. The error was : ${error.message}`);
  };

  const handleDeleteFDSConnectionComplete = () => {
    toastSuccess('Successfully unmapped integration connection');
  };

  const { classes, ledgerId, clientId, isQuickAdd, onClose } = props;
  const isEditingExistingLedger = !!ledgerId;

  const handleSaveAndFinish = useCallback(() => {
    toastSuccess(`Successfully ${ledgerId ? 'updated' : 'created'} ledger account`);

    if (shouldFinishAndClose && !isMappingConnection) {
      if (isQuickAdd) onClose();
      else hideModalToRefetchData();
    }
  }, [shouldFinishAndClose, isQuickAdd, isMappingConnection, onClose, ledgerId]);

  // Handle modal and toasts once mutations are complete
  useEffect(() => {
    if (isMutationComplete) {
      setIsMutationComplete(false); // reset between saves
      handleSaveAndFinish();
    }
  }, [isMutationComplete, handleSaveAndFinish]);

  return (
    <EditLedgerContainer>
      <AppBar color="primary" position="relative">
        <Toolbar>
          <Typography align="left" color="inherit" className={classes.title}>
            {isEditingExistingLedger ? 'Update' : 'Create'} ledger
          </Typography>
          <IconButton
            color="inherit"
            onClick={isQuickAdd ? onClose : handleClose}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Query query={GET_LEDGER_ACCOUNT} skip={!isEditingExistingLedger} variables={{ ledgerId }}>
        {({ data, loading: ledgerLoading }) => {
          const ledgerAccount = data?.ledgerAccount;

          if (isEditingExistingLedger && ledgerLoading) {
            return (
              <Grid container alignItems="center" justifyContent="center">
                <Spinner />
              </Grid>
            );
          }
          return (
            <Mutation
              mutation={MAP_CONNECTION_TO_ACCOUNT}
              onCompleted={handleConnectionMappingComplete}
            >
              {(mapConnectionToAccount, { loading: connectionLoading }) => (
                <Mutation
                  mutation={UNMAP_FDS_ACCOUNT_LEDGER}
                  onCompleted={handleDeleteFDSConnectionComplete}
                >
                  {(unmapFDSAccountLedger, { loading: unmapFDSAccountLedgerLoading }) => (
                    <Mutation
                      mutation={CREATE_LEDGER_ACCOUNT}
                      onError={handleError}
                      onCompleted={() => setIsMutationComplete(true)}
                    >
                      {(createLedgerAccount, { loading: createLoading }) => (
                        <Mutation
                          mutation={UPDATE_LEDGER_ACCOUNT}
                          onError={handleError}
                          onCompleted={() => setIsMutationComplete(true)}
                        >
                          {(updateLedgerAccount, { loading: updateLoading }) => (
                            <LedgerOptionsDataProvider clientId={clientId}>
                              <LedgerOptionsDataConsumer>
                                {referenceData => (
                                  <LedgerForm
                                    onFormSubmit={(newData, isSaveAndFinish) =>
                                      handleFormSubmit(
                                        newData,
                                        createLedgerAccount,
                                        updateLedgerAccount,
                                        mapConnectionToAccount,
                                        referenceData,
                                        unmapFDSAccountLedger,
                                        isSaveAndFinish,
                                      )
                                    }
                                    loading={
                                      createLoading ||
                                      updateLoading ||
                                      connectionLoading ||
                                      unmapFDSAccountLedgerLoading
                                    }
                                    ledgerId={ledgerId}
                                    clientId={clientId}
                                    ledgerAccount={ledgerAccount}
                                    closeModal={isQuickAdd ? onClose : handleClose}
                                    referenceData={referenceData}
                                    isQuickAdd={isQuickAdd}
                                  />
                                )}
                              </LedgerOptionsDataConsumer>
                            </LedgerOptionsDataProvider>
                          )}
                        </Mutation>
                      )}
                    </Mutation>
                  )}
                </Mutation>
              )}
            </Mutation>
          );
        }}
      </Query>
    </EditLedgerContainer>
  );
};

EditLedger.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  classes: PropTypes.object.isRequired,
  ledgerId: PropTypes.number,
  clientId: PropTypes.number.isRequired,
  isQuickAdd: PropTypes.bool,
  onClose: PropTypes.func,
};

EditLedger.defaultProps = {
  ledgerId: undefined,
  isQuickAdd: false,
  onClose: () => {},
};

export default withStyles(materialStyles)(EditLedger);
